import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Calendar, ChevronDown, Menu, Phone, X } from "lucide-react";

type MenuKey = "lead-gen" | "custom-apps" | "websites" | "visibility" | "contact";

type NavLink = { label: string; description: string; to?: string; href?: string };

const NAV_GROUPS: { key: MenuKey; label: string; links: NavLink[] }[] = [
  {
    key: "lead-gen" as const,
    label: "Lead Gen Tools",
    links: [
      { to: "/lead-generation-tools", label: "Lead Generation Tools", description: "Calculators, analyzers and interactive reports" },
      { to: "/lead-generation-calculator", label: "Lead Generation Calculators", description: "Valuation, ROI and savings tools that capture leads" },
      { to: "/assessments", label: "Assessments", description: "Score and qualify prospects before the call" },
      { to: "/visualizers", label: "Visualizers", description: "Help buyers preview products in their own space" },
      { to: "/idea-generator", label: "Idea Generator", description: "Brainstorm tool and app ideas for your business" },
    ],
  },
  {
    key: "websites" as const,
    label: "Websites",
    links: [
      { to: "/event-landing-pages", label: "Landing Pages", description: "Campaign, launch and event experiences" },
      { to: "/squarespace-websites", label: "Squarespace Sites", description: "New builds, redesigns and migrations" },
    ],
  },
  {
    key: "custom-apps" as const,
    label: "Custom Apps",
    links: [
      { to: "/custom-app-builds", label: "Custom App Builds", description: "Full web applications built around your business" },
    ],
  },
  {
    key: "visibility" as const,
    label: "Promotion & Visibility",
    links: [
      { to: "/linkedin-advertising", label: "LinkedIn Advertising", description: "Managed campaigns for B2B buyers" },
      { to: "/seo-ai-visibility", label: "SEO & AI Visibility", description: "Be found in search and AI answers" },
    ],
  },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openMenu) return;
    const closeOnOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (mobileOpen) return;
      if (navRef.current && !navRef.current.contains(event.target as Node)) setOpenMenu(null);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("touchstart", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("touchstart", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileOpen, openMenu]);

  const toggleMenu = (menu: MenuKey) => setOpenMenu((current) => (current === menu ? null : menu));
  const closeAll = () => {
    window.setTimeout(() => {
      setOpenMenu(null);
      setMobileOpen(false);
    }, 0);
  };

  return (
    <>
      <header className="sticky top-0 z-[70] w-full border-b border-border/30 bg-background/80 px-5 py-4 backdrop-blur-md sm:px-6 sm:py-5 md:px-8 lg:px-12 lg:py-6">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="inline-flex items-center" aria-label="Square Migration home">
            <img src="https://apps.squaremigration.com/images/design-mode/logofile.png" alt="Square Migration" className="h-8 w-auto sm:h-10" />
          </Link>

          <div ref={navRef} className="hidden items-center gap-4 lg:flex">
            <nav className="flex items-center gap-4" aria-label="Main navigation">
              {NAV_GROUPS.map((group) => {
                const isOpen = openMenu === group.key;
                return (
                  <div className="relative" key={group.key}>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                      onClick={() => toggleMenu(group.key)}
                      aria-expanded={isOpen}
                      aria-haspopup="menu"
                    >
                      {group.label}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                    </button>
                    {isOpen && (
                      <div className="absolute left-0 top-full z-50 mt-3 w-72 overflow-hidden rounded-lg border border-border/60 bg-background/95 shadow-xl backdrop-blur-md" role="menu">
                        {group.links.map((link, index) =>
                          link.href ? (
                            <a
                              key={link.label}
                              href={link.href}
                              target="_blank"
                              rel="noreferrer"
                              role="menuitem"
                              className={`block px-4 py-3.5 transition-colors hover:bg-foreground/5 ${index ? "border-t border-border/30" : ""}`}
                            >
                              <span className="block text-sm font-semibold text-foreground">{link.label}</span>
                              <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{link.description}</span>
                            </a>
                          ) : (
                            <Link
                              key={link.label}
                              to={link.to!}
                              role="menuitem"
                              className={`block px-4 py-3.5 transition-colors hover:bg-foreground/5 ${index ? "border-t border-border/30" : ""}`}
                            >
                              <span className="block text-sm font-semibold text-foreground">{link.label}</span>
                              <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{link.description}</span>
                            </Link>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <Link to="/about" className="whitespace-nowrap text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
                About
              </Link>
            </nav>

            <div className="relative">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-foreground/30 px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-foreground/10"
                onClick={() => toggleMenu("contact")}
                aria-expanded={openMenu === "contact"}
                aria-haspopup="menu"
              >
                Contact
                <ChevronDown className={`h-4 w-4 transition-transform ${openMenu === "contact" ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {openMenu === "contact" && (
                <div className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-border/60 bg-background/95 shadow-xl backdrop-blur-md" role="menu">
                  <a href="tel:+16127101772" className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground transition-colors hover:bg-foreground/5 hover:text-primary" onClick={closeAll} role="menuitem">
                    <Phone className="h-4 w-4 text-primary" />
                    <span>Call or Text<span className="block text-xs font-normal text-muted-foreground">612-710-1772</span></span>
                  </a>
                  <a href="https://calendly.com/cgould-mn/new-meeting" target="_blank" rel="noreferrer" className="flex items-center gap-3 border-t border-border/30 px-4 py-3.5 text-sm font-medium text-foreground transition-colors hover:bg-foreground/5 hover:text-primary" onClick={closeAll} role="menuitem">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span>Meet Virtually<span className="block text-xs font-normal text-muted-foreground">Book a time on Calendly</span></span>
                  </a>
                </div>
              )}
            </div>
          </div>

          <button type="button" className="relative inline-flex items-center justify-center rounded-full border border-border/50 p-2 text-foreground transition-colors hover:bg-foreground/5 lg:hidden" onClick={() => setMobileOpen((current) => !current)} aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen}>
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <div className={`fixed inset-0 z-[60] flex flex-col overflow-y-auto bg-background transition-opacity duration-300 ease-out lg:hidden ${mobileOpen ? "pointer-events-auto opacity-100" : "pointer-events-none invisible opacity-0"}`} aria-hidden={!mobileOpen} inert={!mobileOpen}>
        <div className="flex-1 px-5 pb-6 pt-20 sm:px-8 sm:pt-24">
          <nav aria-label="Mobile navigation" className="mx-auto max-w-lg">
            <div className="divide-y divide-border/40 border-y border-border/40">
              {NAV_GROUPS.map((group) => {
                const isOpen = openMenu === group.key;
                return (
                  <div key={group.key}>
                    <button type="button" className="flex w-full items-center justify-between py-4 text-left text-xl font-semibold text-foreground sm:py-5 sm:text-2xl" onClick={() => toggleMenu(group.key)} aria-expanded={isOpen}>
                      {group.label}
                      <ChevronDown className={`h-5 w-5 text-primary transition-transform ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                    </button>
                    {isOpen && (
                      <div className="pb-5 pl-4">
                        {group.links.map((link) =>
                          link.href ? (
                            <a key={link.label} href={link.href} target="_blank" rel="noreferrer" className="block border-l border-primary/40 py-2.5 pl-4 text-base font-medium text-muted-foreground transition-colors hover:text-primary">
                              {link.label}
                            </a>
                          ) : (
                            <Link key={link.label} to={link.to!} className="block border-l border-primary/40 py-2.5 pl-4 text-base font-medium text-muted-foreground transition-colors hover:text-primary">
                              {link.label}
                            </Link>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <Link to="/about" className="block py-4 text-xl font-semibold text-foreground transition-colors hover:text-primary sm:py-5 sm:text-2xl">About</Link>
            </div>
          </nav>
        </div>

        <div className="border-t border-border/30 px-5 pb-6 pt-5 sm:px-8 sm:pb-10 sm:pt-6">
          <div className="mx-auto max-w-lg">
            <p className="mb-4 text-sm text-muted-foreground">Ready to turn your content into a tool people actually use?</p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a href="https://calendly.com/cgould-mn/new-meeting" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full border border-foreground/30 px-8 py-4 text-sm font-semibold text-foreground transition-colors hover:bg-foreground/10" onClick={closeAll}>Meet Virtually</a>
              <a href="tel:+16127101772" className="inline-flex items-center justify-center gap-2 rounded-full border border-foreground/30 px-8 py-4 text-sm font-semibold text-foreground transition-colors hover:bg-foreground/10" onClick={closeAll}><Phone className="h-4 w-4" />Call or Text 612-710-1772</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
